import React, { useContext } from 'react';
import { useStore } from 'zustand';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';

import {
  InventoryTabItemName,
  inventoryStore,
} from '../../../../../InventoryStore';
import { useInventoryState } from '../../../../../hooks';
import { useReadOnlyRowConfig } from '../../../../../hooks/useReadOnlyRowConfig';
import { isDateAfterEndOfSale, isDatePast } from '../../../../../utils';
import type { PackDetails } from '../../../../../utils/packs';
import { PackType } from '../../../../../utils/packs';
import { PackType as TabPackType } from '../../../../Header/ActionsBar/InventoryToggle/InventoryToggle';
import { LabelCell } from '../../../../Table/Cells/LabelCell/LabelCell';
import { LabelValueCell } from '../../../../Table/Cells/LabelValueCell/LabelValueCell';
import { PureFieldCell } from '../../../../Table/Cells/PureFieldCell/PureFieldCell';
import { TableRow } from '../../../../Table/TableRow/TableRow';
import { formatTooltip } from '../utils';

const RoomPackagePriceDiscountRowComponent: React.FC<{
  packId: number;
  roomId?: number;
}> = ({ packId, roomId }) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { roomsList, experiencesList, originalsList } =
    useContext(ModernHotelContext);
  const { data: experiencesCategories } = useExperiencesCategories();
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);

  const { inventoryPacks, dateKeys } = useReadOnlyRowConfig(
    InventoryTabItemName.Packs
  );

  const { endOfSaleDate, packType } = useInventoryState();

  if (!inventoryPacks) {
    return null;
  }

  return (
    <TableRow>
      {packType === TabPackType.NIGHT_STAY ? (
        <LabelCell
          label="Package price"
          hasInfoIcon
          tooltipTitle="The Package price and discount are calculated by combining the room and activity prices and discounts."
        />
      ) : (
        <LabelCell label="Price /2 pers." />
      )}

      {dateKeys.map((dateKey) => {
        const fieldName = [dateKey, `${packId}`];
        const bookedStockFieldName = [...fieldName, ''];
        const isGrayed =
          isDatePast(dateKey) || isDateAfterEndOfSale(dateKey, endOfSaleDate);

        return (
          <PureFieldCell key={dateKey} name={fieldName} dateKey={dateKey}>
            {(values?: PackDetails) => {
              if (!values) {
                return null;
              }

              const roomDetails =
                roomId && values.type === PackType.WITH_ROOM
                  ? values.roomsDetails?.find((r) => r.roomId === roomId)
                  : values.type === PackType.WITHOUT_ROOM
                  ? values.priceDetails
                  : undefined;

              const hasSellableStock =
                values.type === PackType.WITH_ROOM && values.roomsDetails
                  ? (values.roomsDetails.find((r) => r.roomId === roomId)
                      ?.sellableStock ?? 0) > 0
                  : true;

              const roomStatus =
                values.type === PackType.WITH_ROOM
                  ? values.roomsDetails?.find((r) => r.roomId === roomId)
                      ?.inventoryStatus
                  : undefined;
              const isRoomNotOnSale =
                roomStatus === 'CLOSED' || roomStatus === 'UNPUBLISHED';

              return (
                <LabelValueCell
                  value={roomDetails?.discountedTotalPrice ?? 0}
                  label={
                    roomDetails?.discount !== undefined
                      ? `-${roomDetails.discount}%`
                      : undefined
                  }
                  labelPosition="bottom"
                  date={dateKey}
                  formatter={() =>
                    roomDetails?.discountedTotalPrice !== undefined
                      ? currencyFormatter(
                          !isEditMode
                            ? Math.floor(roomDetails.discountedTotalPrice)
                            : roomDetails.discountedTotalPrice
                        )
                      : '-'
                  }
                  name={bookedStockFieldName}
                  grayed={isGrayed || isRoomNotOnSale}
                  disabled={
                    values.type === PackType.WITH_ROOM && !hasSellableStock
                  }
                  tooltip={formatTooltip(
                    values,
                    currencyFormatter,
                    roomsList,
                    [...experiencesList, ...originalsList],
                    experiencesCategories,
                    roomId
                  )}
                />
              );
            }}
          </PureFieldCell>
        );
      })}
    </TableRow>
  );
};

export const RoomPackagePriceDiscountRow = React.memo(
  RoomPackagePriceDiscountRowComponent
);
